/**
 * publishRecordVideoUrl.ts
 *
 * Resolve the downloadable/playable URL of a succeeded `PublishRecord`. The
 * download URL is never stored — it is derived from `outputJson.video.fileKey`
 * on read — so every surface that hands a translated render to the browser has
 * to run the same two-branch rule. It lived inline in `usePublishRecordDownload`
 * and again in `usePublishedClipSource`; the grid's bulk Download (PLAT-11660)
 * would have been the third copy.
 *
 * "This record has no artifact" is an ordinary answer rather than a failure —
 * a record still rendering has none — so this returns `null` rather than a
 * `Result`, matching `publishedRenderSelection` beside it. A failed signing
 * request resolves to `null` too; `requestPublishRecordResources` never throws.
 */

import { requestPublishRecordResources } from '@src/services/VideoResourcesProvider';

import type { PublishRecord } from '@src/types.ts/publishRecord';

export async function resolvePublishRecordVideoUrl(
  record: PublishRecord,
): Promise<string | null> {
  const fileKey = record.outputJson?.video?.fileKey;
  if (!fileKey) return null;

  // Synthetic legacy records store the direct download URL in fileKey; real
  // records need a signed URL resolved from the backend.
  if (record.id.startsWith('legacy-')) return fileKey;

  const resources = await requestPublishRecordResources(record.id, ['video']);
  return resources.video?.url ?? null;
}
